import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Box, Typography, Paper, Alert, CircularProgress, LinearProgress,
  Button, Stack, Autocomplete, TextField
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import FileUploader from '../components/dc/FileUploader'
import { dcApi } from '../services/dc.api'
import { Distributor } from '../types/dc.types'

export default function DCPurchaseUploadPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [selectedDistributor, setSelectedDistributor] = useState<Distributor | null>(null)

  const { data: distributors = [], isLoading: distributorsLoading } = useQuery({
    queryKey: ['distributors'],
    queryFn: () => dcApi.getDistributors()
  })

  const uploadMutation = useMutation({
    mutationFn: (file: File) =>
      dcApi.uploadPurchaseBill(file, selectedDistributor?.id, selectedDistributor?.distributor_name),
    onSuccess: (data) => {
      navigate(`/purchases/${data.purchaseId}`)
    },
    onError: (err: Error) => {
      setError(err.message)
    }
  })

  const handleFileUpload = useCallback((file: File) => {
    if (!selectedDistributor) {
      setError('Please select a distributor first')
      return
    }
    setError(null)
    uploadMutation.mutate(file)
  }, [uploadMutation, selectedDistributor])

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/purchases')}>
          Back
        </Button>
        <Typography variant="h4">Upload Purchase Bill</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {uploadMutation.isPending ? (
        <Paper sx={{ p: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={3} py={4}>
            <CircularProgress size={48} />
            <Typography variant="h6">Processing purchase bill...</Typography>
            <Typography variant="body2" color="text.secondary">
              Parsing file, matching items, and calculating totals. This may take a moment.
            </Typography>
            <LinearProgress sx={{ width: '100%', maxWidth: 400 }} />
          </Box>
        </Paper>
      ) : (
        <Stack spacing={3}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Step 1: Select Distributor
            </Typography>
            <Autocomplete
              value={selectedDistributor}
              onChange={(_, newValue) => setSelectedDistributor(newValue)}
              options={distributors}
              getOptionLabel={(option) => option.distributor_name}
              loading={distributorsLoading}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Distributor"
                  placeholder="Search distributors..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {distributorsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography>{option.distributor_name}</Typography>
                </li>
              )}
              sx={{ maxWidth: 500 }}
            />
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Step 2: Upload CSV File
            </Typography>
            <FileUploader
              onUpload={handleFileUpload}
              isLoading={false}
              acceptedTypes={['.csv']}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Upload a distributor purchase bill CSV. Supports c2code format (Alpha Pharma, Kaveri Medicals) and Muthu Medicals format. The system will automatically detect the format, parse items, and match them to the medicine master.
            </Typography>
          </Paper>
        </Stack>
      )}
    </Box>
  )
}
